import { EntityTypes } from './../../context/entityContext';
import kyWithSentry from './helper/kyWithSentry';

import getChartsTrendUrl from 'url-constructors/getChartsTrendURL';

import { ITopicFilterState } from 'components/TopicFilters/shared';
import { IEntity } from 'context/entityContext';
import { ChartTypes, CountryIds } from 'types/shared';
import getValidChartDotsNumber from 'components/data-visualization/DoubleChart/utils/getValidChartDotsNumber';

export interface ISelectedItem {
  id: number;
  countryId: string;
  chartFrequency: 'daily' | 'weekly';
  customerId: number;
  productId: string;
  productType: EntityTypes;
}

export interface IChartInterceptorData extends IEntity {
  selectedItem: ISelectedItem;
  chartType: ChartTypes;
}

export interface IChartDataPoint {
  periodKey: number;
  position?: Date | number | null;
  streamCount?: Date | number | null;
}

interface IChart {
  chartDisplayName: string;
  chartId: number;
  chartName: string;
  customerId: number;
  customerName: string;
  data: IChartDataPoint[];
  peakPosition: number;
  totalStreamCountForPeriod: number | null;
}

interface ITrendChartData {
  charts: IChart[];
  countryIds: CountryIds;
  entity: any;
}

const getCountryIds = (
  filters: ITopicFilterState | undefined,
  selection: ISelectedItem
): string => selection?.countryId ?? filters?.country ?? 'WW';

const getChartsData = (data: ITrendChartData[]): IChartDataPoint[] => {
  let chartData: IChartDataPoint[] = [];
  if (data) {
    data.forEach(({ charts }: { charts: IChart[] }) => {
      if (charts.length > 0) {
        chartData = charts[0].data;
      }
    });
  }
  return chartData;
};

const getParam = (data: IChartDataPoint[], key: string): IChartDataPoint[] =>
  data.map((chart: IChartDataPoint) => ({
    periodKey: chart.periodKey,
    [`${key}`]: (chart as any)[key],
  }));

const chartExpandedTrendInterceptor = async (
  _input: RequestInfo /* eslint-disable-line */,
  _init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData: IChartInterceptorData
): Promise<Response> => {
  const {
    entityIds,
    entityType,
    chartType,
    filters,
    selectedItem,
  } = interceptionData;
  const dotsNumber = selectedItem.chartFrequency === 'weekly' ? 52 : 30;

  const withTrackColumn = entityType === 'artist' || entityType === 'project';

  const apiUrl = getChartsTrendUrl({
    entityIds: withTrackColumn ? [selectedItem.productId] : entityIds,
    entityType: withTrackColumn ? selectedItem.productType : entityType,
    chartType,
    chartIds: selectedItem?.id,
    chartFrequency: selectedItem?.chartFrequency,
    countryIds: getCountryIds(filters, selectedItem),
  });

  const chartTrendData = await kyWithSentry
    .get(apiUrl)
    .json<ITrendChartData[]>();

  try {
    const chartsData = getChartsData(chartTrendData);
    const validChartsData = getValidChartDotsNumber(chartsData, dotsNumber);
    const periodKeyList = validChartsData.map(
      ({ periodKey }: { periodKey: Date | number }) => periodKey
    );
    const positions = getParam(validChartsData, 'position');
    const streams = getParam(validChartsData, 'streamCount');

    // Are all positions null? We don't have any data.
    const mainHasNoPositions =
      positions &&
      positions.every(
        (d: IChartDataPoint) => d.position === null || d.position === 0
      );
    // Are all values null? We don't have any complementary data.
    const complementaryHasNoStreams =
      streams &&
      streams.every(
        (d: IChartDataPoint) => d.streamCount === null || d.streamCount === 0
      );

    const response = new Response();
    response.json = (): Promise<any> =>
      new Promise((resolve) =>
        resolve({
          hasNoValues: {
            position: mainHasNoPositions,
            stream: complementaryHasNoStreams,
          },
          periodKeyList,
          streams,
          positions,
          trends: validChartsData,
        })
      );
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export default chartExpandedTrendInterceptor;
